import { TIME_FORMAT } from "@gymflow/validation";
import range from "lodash/range";
import moment from "moment-timezone";
import { useMemo } from "react";

import { PaginatedSelectOption } from "../../atoms";
import { TimeLimitSelect } from "./TimeLimitSelect";

type TimeIntervalProps = {
  startTime: string;
  endTime: string | null;
  minTime?: string;
  onChange: ({
    startTime,
    endTime,
  }: {
    startTime: string;
    endTime: string | null;
  }) => void;
  isDisabled?: boolean;
};

const MAX_MENU_HEIGHT = 240;

export function TimeInterval({
  startTime,
  endTime,
  minTime = "00:00",
  onChange,
  isDisabled = false,
}: TimeIntervalProps) {
  // Refactor this logic after refactoring on BE side.
  // More in info in the comment from BE:
  // https://linear.app/gymflow/issue/GYM-1131/[be]-improve-availability-on-staff-portalstaff-app#comment-f0ae42fa
  // and
  // https://linear.app/gymflow/issue/GYM-2233/allow-selecting-end-of-day-for-availability
  const customOptions = useMemo(() => {
    if (startTime === "23:55") {
      return [{ label: "12:00 AM", value: null }];
    }

    const values: PaginatedSelectOption[] = [];
    const hours = range(0, 24);
    const minutes = range(0, 60, 5);
    const minTime = moment(startTime, "HH:mm")
      .add(5, "minutes")
      .format("HH:mm");

    // copy-past from TimeLimitSelect component
    hours.forEach((h) => {
      minutes.forEach((m) => {
        const hourString = h.toString().padStart(2, "0");
        const minuteString = m.toString().padStart(2, "0");
        const option = `${hourString}:${minuteString}`;
        if (moment(option, "HH:mm").isBefore(moment(minTime, "HH:mm"))) {
          return;
        }
        values.push({
          label: moment(option, "HH:mm").format("h:mm A"),
          value: option,
        });
      });
    });
    return [...values, { label: "12:00 AM", value: null }];
  }, [startTime]);

  return (
    <div className="flex items-center gap-2">
      <div>
        <TimeLimitSelect
          value={startTime}
          menuPortalTargetDocumentBody
          maxMenuHeight={MAX_MENU_HEIGHT}
          menuPlacement="top"
          minTime={minTime}
          onChange={(newTime) => {
            const startMoment = moment(newTime, "HH:mm");
            const endMoment = moment(
              endTime === null ? "24:00" : endTime,
              "HH:mm",
            );
            if (startMoment.isBefore(endMoment)) {
              onChange({ startTime: newTime, endTime: endTime });
            } else {
              onChange({
                startTime: newTime,
                endTime:
                  startMoment.format("HH:mm") === "23:55"
                    ? null
                    : startMoment.add(5, "minutes").format("HH:mm"),
              });
            }
          }}
          isDisabled={isDisabled}
        />
      </div>
      <div className="h-px w-4 bg-gray-200"></div>
      <div>
        <TimeLimitSelect
          value={
            endTime === null
              ? "24:00"
              : moment(endTime, TIME_FORMAT).format("HH:mm")
          }
          menuPortalTargetDocumentBody
          maxMenuHeight={MAX_MENU_HEIGHT}
          menuPlacement="top"
          minTime={moment(startTime, "HH:mm").add(5, "minutes").format("HH:mm")}
          onChange={(newTime) => {
            onChange({ startTime: startTime, endTime: newTime });
          }}
          isDisabled={isDisabled}
          customOptions={customOptions}
        />
      </div>
    </div>
  );
}
